import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../common/ConfirmDialog';
import JobService from '../../services/JobService';
import { errorMessage } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { fmtDateTime } from '../../utils/format';

/**
 * Notes on a job (bilješke): newest first, with author and time. Everyone can add; authors edit their own,
 * authors and admins delete (the server decides via canEdit/canDelete). Rendered as a tab of JobHistoryCard:
 * `onCount` reports the number of notes for the tab label, `onChanged` refreshes the activity tab.
 */
export default function NotesPanel({ jobId, reloadKey, onChanged, onCount }) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [notes, setNotes] = useState([]);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(null); // { id, body }
    const [toDelete, setToDelete] = useState(null);

    const load = useCallback(() => JobService.notes(jobId).then(n => { setNotes(n); onCount?.(n.length); }).catch(() => {}), [jobId, onCount]);
    useEffect(() => { load(); }, [load, reloadKey]);

    const run = async (fn, okMessage) => {
        setSaving(true);
        try {
            await fn();
            await load();
            onChanged?.();
            if (okMessage) showToast(okMessage, 'success');
            return true;
        } catch (err) {
            showToast(errorMessage(err, t('common.error')), 'error');
            return false;
        } finally {
            setSaving(false);
        }
    };

    const add = async () => {
        if (!draft.trim()) return;
        if (await run(() => JobService.addNote(jobId, draft), t('notes.added'))) setDraft('');
    };
    const saveEdit = async () => {
        if (!editing.body.trim()) return;
        if (await run(() => JobService.updateNote(jobId, editing.id, editing.body))) setEditing(null);
    };

    return (
        <>
            <Box sx={{ px: 2.5, pt: 2, pb: notes.length ? 1 : 2 }}>
                <TextField fullWidth multiline minRows={2} size="small" placeholder={t('notes.placeholder')} value={draft}
                           onChange={(e) => setDraft(e.target.value)} slotProps={{ htmlInput: { maxLength: 10000 } }}
                           onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) add(); }} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button size="small" variant="contained" disableElevation onClick={add} disabled={saving || !draft.trim()}>
                        {saving && !editing ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : t('notes.add')}
                    </Button>
                </Box>
            </Box>
            {notes.map(n => (
                <Box key={n.id} sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #F1F5F9' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1a2332' }}>{n.authorName}</Typography>
                        <Typography sx={{ fontSize: 12, color: '#94A3B8', flex: 1 }}>
                            {fmtDateTime(n.createdAt)}{n.updatedAt ? ` · ${t('notes.edited')}` : ''}
                        </Typography>
                        {n.canEdit && editing?.id !== n.id && (
                            <Tooltip title={t('common.edit')}>
                                <IconButton size="small" onClick={() => setEditing({ id: n.id, body: n.body })}><EditOutlinedIcon sx={{ fontSize: 17 }} /></IconButton>
                            </Tooltip>
                        )}
                        {n.canDelete && (
                            <Tooltip title={t('common.delete')}>
                                <IconButton size="small" onClick={() => setToDelete(n)} sx={{ '&:hover': { color: '#EF4444' } }}><DeleteOutlineIcon sx={{ fontSize: 17 }} /></IconButton>
                            </Tooltip>
                        )}
                    </Box>
                    {editing?.id === n.id ? (
                        <Box sx={{ mt: 1 }}>
                            <TextField fullWidth multiline minRows={2} size="small" autoFocus value={editing.body}
                                       onChange={(e) => setEditing({ ...editing, body: e.target.value })} slotProps={{ htmlInput: { maxLength: 10000 } }} />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                                <Button size="small" onClick={() => setEditing(null)} disabled={saving} sx={{ color: '#64748B' }}>{t('common.cancel')}</Button>
                                <Button size="small" variant="contained" disableElevation onClick={saveEdit} disabled={saving || !editing.body.trim()}>{t('common.save')}</Button>
                            </Box>
                        </Box>
                    ) : (
                        <Typography sx={{ fontSize: 13.5, color: '#334155', whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.5 }}>{n.body}</Typography>
                    )}
                </Box>
            ))}
            <ConfirmDialog open={!!toDelete} title={t('notes.deleteTitle')} text={t('notes.deleteText')}
                           onConfirm={async () => { await run(() => JobService.deleteNote(jobId, toDelete.id)); setToDelete(null); }}
                           onClose={() => setToDelete(null)} />
        </>
    );
}
